import { Button } from '@clearpath/design-system/ui/button';
import {
  OrderType,
  useFederationInboxQuery,
  type FederationInboxOrderFragment,
  type FederationInboxRowFragment,
} from "../__generated__/graphql";
import { FederationInboxRow } from "../components/FederationInboxRow";

/**
 * Outbox — historical federated work this vendor has already returned.
 *
 * Uses the same FederationInbox query but filters to terminal statuses.
 * Once the SDL exposes a dedicated `federationOutbox` query with richer
 * analytics fields (turnaround time, reject rate, etc.) we'll switch.
 */
export function OutboxPage() {
  const { data, loading, error, refetch } = useFederationInboxQuery({
    variables: {
      filter: {
        orderType: [OrderType.FederatedIncoming],
        status: ['COMPLETED', 'CANCELLED'],
      },
      page: { first: 100 },
    },
  });

  if (loading && !data) {
    return (
      <section aria-label="Outbox" className="inbox-page">
        <p className="loading-row">Loading outbox…</p>
      </section>
    );
  }
  if (error) {
    return (
      <section aria-label="Outbox" className="inbox-page">
        <div className="notice notice-danger" role="alert">
          <p>Couldn’t load your outbox.</p>
          <Button type="button" size="sm" variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </section>
    );
  }

  const orders = data?.orders.edges ?? [];
  const rows: Array<{
    order: FederationInboxOrderFragment;
    step: FederationInboxRowFragment;
  }> = orders.flatMap(({ node: order }) =>
    (order.stepRuns?.edges ?? []).map(({ node: step }) => ({ order, step })),
  );

  return (
    <section aria-label="Outbox" className="inbox-page">
      <header className="page-head">
        <div>
          <h1>Outbox</h1>
          <p className="page-sub">Completed and returned federated work.</p>
        </div>
      </header>

      {rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon" aria-hidden="true">
            ⏱
          </div>
          <h2>No returned work yet</h2>
          <p>
            Once you complete and return federated cases, they’ll be archived
            here as your track record with each partner lab.
          </p>
        </div>
      ) : (
        <div className="table-card">
          <table className="inbox">
            <thead>
              <tr>
                <th scope="col">Origin</th>
                <th scope="col">Order &amp; case</th>
                <th scope="col">Work type</th>
                <th scope="col">SLA</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ order, step }) => (
                <FederationInboxRow key={step.id} order={order} step={step} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
